import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../contexts/ApiContext';
import { useWebSocket } from '../contexts/WebSocketContext';
import { getScoreColors } from '../utils/colorUtils';
import PermissionGate from '../components/PermissionGate';
import { 
  PlusIcon, 
  ChartBarIcon, 
  BoltIcon, 
  LightBulbIcon, 
  ExclamationTriangleIcon,
  ArrowTrendingUpIcon,
  SparklesIcon,
  RocketLaunchIcon
} from '@heroicons/react/24/outline';

interface Problem {
  id: string;
  title: string;
  description: string;
  impact_area: string;
  priority: number;
  stakeholders?: Array<{ name: string; role: string }>;
  metrics?: Array<{
    name: string;
    description: string;
    current_value?: number;
    target_value?: number;
    unit?: string;
  }>;
}

interface Hypothesis {
  id: string;
  title: string;
  hypothesis_statement: string;
  priority_score: number;
  business_impact: number;
  technical_feasibility: number;
  implementation_effort: number;
  problem_id: string;
}

interface DashboardStats {
  projects: number;
  problems: number;
  hypotheses: number;
  avgPriorityScore: number;
}

// Animated Counter Component
const AnimatedCounter: React.FC<{ value: number; duration?: number; decimals?: number }> = ({ 
  value, 
  duration = 2000, 
  decimals = 0 
}) => {
  const [displayValue, setDisplayValue] = useState(0);

  useEffect(() => {
    let startTime: number;
    let animationFrame: number;

    const animate = (currentTime: number) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      
      // Easing function for smooth animation
      const easeOutCubic = 1 - Math.pow(1 - progress, 3);
      const currentValue = easeOutCubic * value;
      
      setDisplayValue(currentValue);
      
      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };

    animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
  }, [value, duration]);

  return (
    <span className="tabular-nums">
      {decimals > 0 ? displayValue.toFixed(decimals) : Math.floor(displayValue)}
    </span>
  );
};

// Animated Progress Bar
const AnimatedProgressBar: React.FC<{ value: number; max: number; color: string; delay?: number }> = ({ 
  value, 
  max, 
  color, 
  delay = 0 
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const percentage = (value / max) * 100;

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), delay);
    return () => clearTimeout(timer);
  }, [delay]);

  return (
    <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
      <div 
        className={`h-full rounded-full transition-all duration-1000 ease-out ${color} ${
          isVisible ? 'opacity-100' : 'opacity-0'
        }`}
        style={{ 
          width: isVisible ? `${percentage}%` : '0%',
          transformOrigin: 'left center'
        }}
      />
    </div>
  );
};

const DashboardPage: React.FC = () => {
  const { get } = useApi();
  const { socket, isConnected } = useWebSocket();
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats>({
    projects: 0,
    problems: 0,
    hypotheses: 0,
    avgPriorityScore: 0
  });
  const [topProblems, setTopProblems] = useState<Problem[]>([]);
  const [topHypotheses, setTopHypotheses] = useState<Hypothesis[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        // Fetch all data in parallel
        const [projects, problems, hypotheses] = await Promise.all([
          get<any[]>('/projects'),
          get<Problem[]>('/problems'),
          get<Hypothesis[]>('/hypotheses')
        ]);


        // Calculate average priority score
        const scores = hypotheses
          .map(h => h.priority_score)
          .filter(score => score && score > 0);
        const avgPriorityScore = scores.length > 0 
          ? scores.reduce((sum, score) => sum + score, 0) / scores.length 
          : 0;

        setStats({
          projects: Array.isArray(projects) ? projects.length : 0,
          problems: Array.isArray(problems) ? problems.length : 0,
          hypotheses: Array.isArray(hypotheses) ? hypotheses.length : 0,
          avgPriorityScore
        });

        // Sort and get top problems (by priority)
        const sortedProblems = [...problems]
          .sort((a, b) => (b.priority || 0) - (a.priority || 0))
          .slice(0, 5);
        setTopProblems(sortedProblems);

        // Sort and get top hypotheses (by priority score)
        const sortedHypotheses = [...hypotheses]
          .sort((a, b) => (b.priority_score || 0) - (a.priority_score || 0))
          .slice(0, 5);
        setTopHypotheses(sortedHypotheses);

        // Trigger animations after data loads
        setTimeout(() => setIsAnimating(true), 300);

      } catch (err) {
        console.error('Error fetching dashboard data:', err);
        setError('Failed to load dashboard data. Please check your server connection.');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [get]);

  // WebSocket event listeners for real-time updates
  useEffect(() => {
    if (!socket || !isConnected) return;

    const handleProjectCreated = () => {
      console.log('Project created, refreshing dashboard data');
      fetchDashboardData();
    };

    const handleProjectUpdated = () => {
      console.log('Project updated, refreshing dashboard data');
      fetchDashboardData();
    };

    const handleProjectDeleted = () => {
      console.log('Project deleted, refreshing dashboard data');
      fetchDashboardData();
    };

    const handleProblemCreated = (data: { problem: Problem }) => {
      console.log('Problem created, updating dashboard');
      setTopProblems(prev => {
        const updated = [...prev, data.problem].sort((a, b) => (b.priority || 0) - (a.priority || 0)).slice(0, 5);
        return updated;
      });
      setStats(prev => ({ ...prev, problems: prev.problems + 1 }));
    };

    const handleProblemUpdated = (data: { problem: Problem }) => {
      console.log('Problem updated, updating dashboard');
      setTopProblems(prev => {
        const updated = prev.map(p => p.id === data.problem.id ? data.problem : p);
        return updated.sort((a, b) => (b.priority || 0) - (a.priority || 0)).slice(0, 5);
      });
    };

    const handleProblemDeleted = (data: { problem_id: string }) => {
      console.log('Problem deleted, updating dashboard');
      setTopProblems(prev => prev.filter(p => p.id !== data.problem_id));
      setStats(prev => ({ ...prev, problems: Math.max(0, prev.problems - 1) }));
    };

    const handleHypothesisCreated = (data: { hypothesis: Hypothesis }) => {
      console.log('Hypothesis created, updating dashboard');
      setTopHypotheses(prev => {
        const updated = [...prev, data.hypothesis].sort((a, b) => (b.priority_score || 0) - (a.priority_score || 0)).slice(0, 5);
        return updated;
      });
      setStats(prev => {
        const newHypothesesCount = prev.hypotheses + 1;
        // Recalculate average priority score
        const allScores = [...topHypotheses, data.hypothesis]
          .map(h => h.priority_score)
          .filter(score => score && score > 0);
        const newAvgScore = allScores.length > 0 
          ? allScores.reduce((sum, score) => sum + score, 0) / allScores.length 
          : 0;
        return { ...prev, hypotheses: newHypothesesCount, avgPriorityScore: newAvgScore };
      });
    };

    const handleHypothesisUpdated = (data: { hypothesis: Hypothesis }) => {
      console.log('Hypothesis updated, updating dashboard');
      setTopHypotheses(prev => {
        const updated = prev.map(h => h.id === data.hypothesis.id ? data.hypothesis : h);
        return updated.sort((a, b) => (b.priority_score || 0) - (a.priority_score || 0)).slice(0, 5);
      });
      // Recalculate average priority score
      setStats(prev => {
        const allScores = topHypotheses
          .map(h => h.id === data.hypothesis.id ? data.hypothesis.priority_score : h.priority_score)
          .filter(score => score && score > 0);
        const newAvgScore = allScores.length > 0 
          ? allScores.reduce((sum, score) => sum + score, 0) / allScores.length 
          : 0;
        return { ...prev, avgPriorityScore: newAvgScore };
      });
    };

    const handleHypothesisDeleted = (data: { hypothesis_id: string }) => {
      console.log('Hypothesis deleted, updating dashboard');
      setTopHypotheses(prev => prev.filter(h => h.id !== data.hypothesis_id));
      setStats(prev => {
        const newHypothesesCount = Math.max(0, prev.hypotheses - 1);
        // Recalculate average priority score
        const remainingHypotheses = topHypotheses.filter(h => h.id !== data.hypothesis_id);
        const allScores = remainingHypotheses
          .map(h => h.priority_score)
          .filter(score => score && score > 0);
        const newAvgScore = allScores.length > 0 
          ? allScores.reduce((sum, score) => sum + score, 0) / allScores.length 
          : 0;
        return { ...prev, hypotheses: newHypothesesCount, avgPriorityScore: newAvgScore };
      });
    };

    // Helper function to refresh all data
    const fetchDashboardData = async () => {
      try {
        const [projects, problems, hypotheses] = await Promise.all([
          get<any[]>('/projects'),
          get<Problem[]>('/problems'),
          get<Hypothesis[]>('/hypotheses')
        ]);

        const scores = hypotheses
          .map(h => h.priority_score)
          .filter(score => score && score > 0);
        const avgPriorityScore = scores.length > 0 
          ? scores.reduce((sum, score) => sum + score, 0) / scores.length 
          : 0;

        setStats({
          projects: Array.isArray(projects) ? projects.length : 0,
          problems: Array.isArray(problems) ? problems.length : 0,
          hypotheses: Array.isArray(hypotheses) ? hypotheses.length : 0,
          avgPriorityScore
        });

        const sortedProblems = [...problems]
          .sort((a, b) => (b.priority || 0) - (a.priority || 0))
          .slice(0, 5);
        setTopProblems(sortedProblems);

        const sortedHypotheses = [...hypotheses]
          .sort((a, b) => (b.priority_score || 0) - (a.priority_score || 0))
          .slice(0, 5);
        setTopHypotheses(sortedHypotheses);
      } catch (err) {
        console.error('Error refreshing dashboard data:', err);
      }
    };

    // Register event listeners
    socket.on('project_created', handleProjectCreated);
    socket.on('project_updated', handleProjectUpdated);
    socket.on('project_deleted', handleProjectDeleted);
    socket.on('problem_created', handleProblemCreated);
    socket.on('problem_updated', handleProblemUpdated);
    socket.on('problem_deleted', handleProblemDeleted);
    socket.on('hypothesis_created', handleHypothesisCreated);
    socket.on('hypothesis_updated', handleHypothesisUpdated);
    socket.on('hypothesis_deleted', handleHypothesisDeleted);

    // Cleanup listeners on unmount
    return () => {
      socket.off('project_created', handleProjectCreated);
      socket.off('project_updated', handleProjectUpdated);
      socket.off('project_deleted', handleProjectDeleted);
      socket.off('problem_created', handleProblemCreated);
      socket.off('problem_updated', handleProblemUpdated);
      socket.off('problem_deleted', handleProblemDeleted);
      socket.off('hypothesis_created', handleHypothesisCreated);
      socket.off('hypothesis_updated', handleHypothesisUpdated);
      socket.off('hypothesis_deleted', handleHypothesisDeleted);
    };
  }, [socket, isConnected, get, topHypotheses]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-indigo-50">
        <div className="text-center">
          <div className="relative">
            <div className="w-16 h-16 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin mx-auto"></div>
            <div className="absolute inset-0 w-16 h-16 border-4 border-transparent border-r-purple-600 rounded-full animate-pulse mx-auto"></div>
          </div>
          <p className="mt-6 text-lg text-gray-600 font-medium">Loading your dashboard...</p>
          <p className="mt-2 text-sm text-gray-400">Preparing insights</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 to-pink-50">
        <div className="max-w-md w-full bg-white/70 backdrop-blur-sm rounded-3xl shadow-2xl p-8 border border-red-100">
          <div className="text-center">
            <ExclamationTriangleIcon className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Connection Error</h2>
            <p className="text-gray-600 mb-4">{error}</p>
            <p className="text-sm text-gray-500">Make sure your FastAPI server is running and accessible</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50 to-purple-50">
      {/* Header */}
      <div className="pt-8 pb-12">
        <div className={`transform transition-all duration-1000 ease-out ${
          isAnimating ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
        }`}>
          <h1 className="text-4xl font-bold text-gray-900 mb-3 tracking-tight">
            AI Value Dashboard
            <SparklesIcon className="inline-block w-8 h-8 ml-3 text-indigo-500" />
          </h1>
          <p className="text-lg text-gray-600 font-medium">
            Discover insights and accelerate your AI initiatives
          </p>
          <div className="mt-4 flex items-center space-x-4">
            <div className={`flex items-center space-x-2 px-3 py-1 rounded-full ${
              isConnected ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
            }`}>
              <div className={`w-2 h-2 rounded-full ${
                isConnected ? 'bg-green-500' : 'bg-red-500'
              } animate-pulse`} />
              <span className="text-sm font-medium">
                {isConnected ? 'Live Data' : 'Offline'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
        {[
          {
            title: 'Business Problems',
            value: stats.problems,
            icon: ExclamationTriangleIcon,
            color: 'from-orange-400 to-red-500',
            bgColor: 'bg-gradient-to-br from-orange-50 to-red-50',
            delay: 0
          },
          {
            title: 'AI Hypotheses',
            value: stats.hypotheses,
            icon: LightBulbIcon,
            color: 'from-yellow-400 to-orange-500',
            bgColor: 'bg-gradient-to-br from-yellow-50 to-orange-50',
            delay: 200
          },
          {
            title: 'Avg Priority Score',
            value: stats.avgPriorityScore,
            icon: ArrowTrendingUpIcon,
            color: 'from-green-400 to-teal-500',
            bgColor: 'bg-gradient-to-br from-green-50 to-teal-50',
            decimals: 2,
            delay: 400
          },
          {
            title: 'Active Projects',
            value: stats.projects,
            icon: RocketLaunchIcon,
            color: 'from-blue-400 to-indigo-500',
            bgColor: 'bg-gradient-to-br from-blue-50 to-indigo-50',
            delay: 600
          }
        ].map((metric, _index) => (
          <div
            key={metric.title}
            className={`transform transition-all duration-700 ease-out ${
              isAnimating 
                ? 'translate-y-0 opacity-100 scale-100' 
                : 'translate-y-12 opacity-0 scale-95'
            }`}
            style={{ transitionDelay: `${metric.delay}ms` }}
          >
            <div className={`relative group cursor-pointer ${metric.bgColor} rounded-3xl p-8 border border-white/20 shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-105 backdrop-blur-sm`}>
              <div className="flex items-center justify-between mb-6">
                <div className={`p-3 rounded-2xl bg-gradient-to-r ${metric.color} shadow-lg`}>
                  <metric.icon className="w-8 h-8 text-white" />
                </div>
                <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="w-3 h-3 bg-gradient-to-r from-indigo-400 to-purple-500 rounded-full animate-pulse" />
                </div>
              </div>
              
              <div className="space-y-3">
                <div className="text-4xl font-bold text-gray-900 tabular-nums">
                  <AnimatedCounter 
                    value={metric.value} 
                    decimals={metric.decimals || 0}
                    duration={1500}
                  />
                </div>
                <p className="text-gray-600 font-medium text-lg">
                  {metric.title}
                </p>
                {metric.title.includes('Score') && (
                  <AnimatedProgressBar 
                    value={metric.value} 
                    max={10} 
                    color={getScoreColors(metric.value).background}
                    delay={metric.delay + 1000}
                  />
                )}
              </div>
              
              {/* Subtle glow effect */}
              <div className={`absolute inset-0 rounded-3xl bg-gradient-to-r ${metric.color} opacity-0 group-hover:opacity-10 transition-opacity duration-300 pointer-events-none`} />
            </div>
          </div>
        ))}
      </div>

      {/* Action Cards */}
      <div className={`grid grid-cols-1 md:grid-cols-3 gap-6 mb-12 transform transition-all duration-1000 ease-out ${
        isAnimating ? 'translate-y-0 opacity-100' : 'translate-y-12 opacity-0'
      }`} style={{ transitionDelay: '800ms' }}>
        {[
          {
            title: 'Create Problem',
            description: 'Define new business challenges',
            icon: PlusIcon,
            color: 'from-indigo-500 to-purple-600',
            action: () => navigate('/app-problems/new')
          },
          {
            title: 'New Hypothesis',
            description: 'Explore AI solutions',
            icon: BoltIcon,
            color: 'from-green-500 to-teal-600',
            action: () => navigate('/app-hypotheses/new')
          },
          {
            title: 'View Matrix',
            description: 'Prioritization insights',
            icon: ChartBarIcon,
            color: 'from-orange-500 to-red-600',
            action: () => navigate('/matrix')
          }
        ].map((action, _index) => (
          <button
            key={action.title}
            onClick={action.action}
            className="group relative bg-white/70 backdrop-blur-sm rounded-3xl p-8 border border-white/20 shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-105 text-left"
          >
            <div className="flex items-center justify-between mb-6">
              <div className={`p-4 rounded-2xl bg-gradient-to-r ${action.color} shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                <action.icon className="w-8 h-8 text-white" />
              </div>
              <div className="opacity-0 group-hover:opacity-100 transition-all duration-300 transform group-hover:translate-x-2">
                <svg className="w-6 h-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </div>
            
            <h3 className="text-2xl font-bold text-gray-900 mb-2 group-hover:text-gray-700 transition-colors duration-300">
              {action.title}
            </h3>
            <p className="text-gray-600 font-medium">
              {action.description}
            </p>
            
            {/* Hover glow effect */}
            <div className={`absolute inset-0 rounded-3xl bg-gradient-to-r ${action.color} opacity-0 group-hover:opacity-5 transition-opacity duration-300 pointer-events-none`} />
          </button>
        ))}
      </div>

      {/* Content Sections */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Top Business Problems */}
        <div className={`transform transition-all duration-1000 ease-out ${
          isAnimating ? 'translate-y-0 opacity-100' : 'translate-y-12 opacity-0'
        }`} style={{ transitionDelay: '1000ms' }}>
          <div className="bg-white/70 backdrop-blur-sm rounded-3xl p-8 border border-white/20 shadow-lg">
            <div className="flex items-center mb-8">
              <ExclamationTriangleIcon className="w-8 h-8 text-orange-500 mr-3" />
              <h2 className="text-2xl font-bold text-gray-900">Top Business Problems</h2>
            </div>
            
            {topProblems.length > 0 ? (
              <div className="space-y-4">
                {topProblems.map((problem, index) => (
                  <div
                    key={problem.id}
                    className={`group cursor-pointer bg-gradient-to-r from-white to-gray-50 rounded-2xl p-6 border border-gray-100 hover:border-orange-200 shadow-sm hover:shadow-lg transition-all duration-300 hover:scale-[1.02] transform ${
                      isAnimating ? 'translate-x-0 opacity-100' : 'translate-x-8 opacity-0'
                    }`}
                    style={{ transitionDelay: `${1200 + index * 100}ms` }}
                    onClick={() => navigate(`/app-problems/${problem.id}`)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 space-y-3">
                        <div className="flex items-center space-x-3">
                          <h3 className="font-bold text-gray-900 group-hover:text-orange-700 transition-colors duration-300">
                            {problem.title}
                          </h3>
                          <span className={`px-3 py-1 text-sm font-medium rounded-full ${getScoreColors(problem.priority || 0).background} ${getScoreColors(problem.priority || 0).text}`}>
                            {problem.priority}/10
                          </span>
                        </div>
                        <p className="text-gray-600 line-clamp-2 leading-relaxed">
                          {problem.description}
                        </p>
                        <div className="flex items-center justify-between">
                          <p className="text-sm text-gray-500 font-medium">
                            Impact: {problem.impact_area}
                          </p>
                          <div className="flex items-center space-x-2 text-xs text-gray-400">
                            {problem.stakeholders && problem.stakeholders.length > 0 && (
                              <span className="bg-green-100 text-green-700 px-2 py-1 rounded-full">
                                {problem.stakeholders.length} stakeholder{problem.stakeholders.length !== 1 ? 's' : ''}
                              </span>
                            )}
                            {problem.metrics && problem.metrics.length > 0 && (
                              <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                                {problem.metrics.length} metric{problem.metrics.length !== 1 ? 's' : ''}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="opacity-0 group-hover:opacity-100 transition-all duration-300 transform group-hover:translate-x-1 ml-4">
                        <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 space-y-4">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto">
                  <ExclamationTriangleIcon className="w-8 h-8 text-orange-500" />
                </div>
                <p className="text-gray-600 font-medium">No business problems defined yet</p>
                <PermissionGate requireWrite>
                  <button
                    onClick={() => navigate('/app-problems/new')}
                    className="text-orange-600 hover:text-orange-700 font-medium transition-colors duration-300"
                  >
                    Add your first problem →
                  </button>
                </PermissionGate>
              </div>
            )}
          </div>
        </div>

        {/* Top AI Hypotheses */}
        <div className={`transform transition-all duration-1000 ease-out ${
          isAnimating ? 'translate-y-0 opacity-100' : 'translate-y-12 opacity-0'
        }`} style={{ transitionDelay: '1200ms' }}>
          <div className="bg-white/70 backdrop-blur-sm rounded-3xl p-8 border border-white/20 shadow-lg">
            <div className="flex items-center mb-8">
              <LightBulbIcon className="w-8 h-8 text-yellow-500 mr-3" />
              <h2 className="text-2xl font-bold text-gray-900">Top AI Hypotheses</h2>
            </div>
            
            {topHypotheses.length > 0 ? (
              <div className="space-y-4">
                {topHypotheses.map((hypothesis, index) => (
                  <div
                    key={hypothesis.id}
                    className={`group cursor-pointer bg-gradient-to-r from-white to-gray-50 rounded-2xl p-6 border border-gray-100 hover:border-yellow-200 shadow-sm hover:shadow-lg transition-all duration-300 hover:scale-[1.02] transform ${
                      isAnimating ? 'translate-x-0 opacity-100' : 'translate-x-8 opacity-0'
                    }`}
                    style={{ transitionDelay: `${1400 + index * 100}ms` }}
                    onClick={() => navigate(`/app-hypotheses/${hypothesis.id}`)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 space-y-4">
                        <div className="flex items-center space-x-3">
                          <h3 className="font-bold text-gray-900 group-hover:text-yellow-700 transition-colors duration-300">
                            {hypothesis.title}
                          </h3>
                          <span className={`px-3 py-1 text-sm font-medium rounded-full ${getScoreColors(hypothesis.priority_score || 0).background} ${getScoreColors(hypothesis.priority_score || 0).text}`}>
                            {hypothesis.priority_score?.toFixed(2) || 'N/A'}
                          </span>
                        </div>
                        <p className="text-gray-600 font-medium italic line-clamp-2 leading-relaxed">
                          {hypothesis.hypothesis_statement}
                        </p>
                        <div className="grid grid-cols-3 gap-4 pt-2">
                          {[
                            { label: 'Impact', value: hypothesis.business_impact, isEffort: false },
                            { label: 'Feasibility', value: hypothesis.technical_feasibility, isEffort: false },
                            { label: 'Effort', value: hypothesis.implementation_effort, isEffort: true }
                          ].map((metric) => (
                            <div key={metric.label} className="text-center">
                              <div className={`text-lg font-bold ${getScoreColors(metric.value || 0, metric.isEffort).textOnly}`}>
                                {metric.value}/10
                              </div>
                              <div className="text-xs text-gray-500 font-medium">
                                {metric.label}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="opacity-0 group-hover:opacity-100 transition-all duration-300 transform group-hover:translate-x-1 ml-4">
                        <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 space-y-4">
                <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto">
                  <LightBulbIcon className="w-8 h-8 text-yellow-500" />
                </div>
                <p className="text-gray-600 font-medium">No AI hypotheses defined yet</p>
                <PermissionGate requireWrite>
                  <button
                    onClick={() => navigate('/app-hypotheses/new')}
                    className="text-yellow-600 hover:text-yellow-700 font-medium transition-colors duration-300"
                  >
                    Create your first hypothesis →
                  </button>
                </PermissionGate>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;